import React, { useEffect, useState, useContext } from 'react';
import { Link, navigate } from 'gatsby';
import styled from 'styled-components';
import { LocalContext, FirebaseContext } from 'context';
import { useLocation } from '@reach/router';
import { breakpoints } from 'styles';
import { scrollToAnchor } from 'utils';
import { motion } from 'framer-motion';

const EventNavbar = ({ eventDate, eventSlug, eid, colors }) => {
  const { theme, isMobile } = useContext(LocalContext);
  const { user } = useContext(FirebaseContext);
  const { pathname } = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState(pathname);

  useEffect(() => setActiveLink(pathname), [pathname]);

  const handleScroll = () => {
    const scrollNavTop = window.scrollY;
    if (scrollNavTop >= 50) {
      setIsScrolled(true);
    } else {
      setIsScrolled(false);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const toggleActiveLink = (link) => {
    setActiveLink(link);
  };

  return (
    <Wrapper
      colors={colors}
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      scrolled={isScrolled}
      theme={theme}>
      <DateContainer dangerouslySetInnerHTML={{ __html: eventDate }} />
      {user?.eventsUserCanAccess?.includes(eid) && (
        <NavLinks
          initial={isMobile ? { opacity: 0, y: -50 } : { opacity: 1, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.75 }}>
          <NavLink
            colors={colors}
            to={`/events/${eventSlug}/livestream/#livestream`}
            className={activeLink === '/livestream' ? 'active' : null}
            onClick={(e) => {
              if (pathname === `/events/${eventSlug}/livestream`) {
                e.preventDefault();
                scrollToAnchor('livestream', {
                  duration: 1200,
                  offset: -160
                });
                toggleActiveLink('/livestream');
              } else {
                e.preventDefault();
                navigate(`/events/${eventSlug}/`);
                scrollToAnchor('livestream', {
                  duration: 1200,
                  offset: -160
                });
              }
            }}>
            Live stream
          </NavLink>
          <NavLink
            colors={colors}
            to={`/events/${eventSlug}/livestream/#speakers`}
            className={activeLink === '/speakers' ? 'active' : null}
            onClick={(e) => {
              if (pathname === `/events/${eventSlug}/livestream`) {
                e.preventDefault();
                scrollToAnchor('speakers', {
                  duration: 1200,
                  offset: -180
                });
                toggleActiveLink('/speakers');
              } else {
                e.preventDefault();
                navigate(`/events/${eventSlug}/`);
                scrollToAnchor('speakers', {
                  duration: 1200,
                  offset: -210
                });
              }
            }}>
            Speakers
          </NavLink>
          {eid !== '11' && eid !== '17' && (
            <NavLink
              colors={colors}
              to={`/events/${eventSlug}/livestream/#info`}
              className={activeLink === '/info' ? 'active' : null}
              onClick={(e) => {
                if (pathname === `/events/${eventSlug}/livestream`) {
                  e.preventDefault();
                  scrollToAnchor('info', {
                    duration: 1200,
                    offset: -75
                  });
                  toggleActiveLink('/info');
                } else {
                  e.preventDefault();
                  navigate(`/events/${eventSlug}/`);
                  scrollToAnchor('info', {
                    duration: 1200,
                    offset: -75
                  });
                }
              }}>
              Our Sponsor
            </NavLink>
          )}
        </NavLinks>
      )}
    </Wrapper>
  );
};

const NavLink = styled(Link)`
  align-items: center;
  color: white;
  cursor: pointer;
  display: flex;
  font-family: 'Noto Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  justify-content: center;
  letter-spacing: 0.5px;
  line-height: 3rem;
  margin-left: 5rem;
  padding-top: 0.125rem;
  text-align: center;
  text-transform: uppercase;
  transition: 0.5s;
  width: 100%;
  ${breakpoints.lg} {
    font-size: 0.875rem;
    line-height: 1.5rem;
    text-transform: uppercase;
    color: white;
    text-decoration: none;
  }
  &:hover {
    text-decoration: underline;
    text-underline-position: under;
  }
  &.active {
    text-decoration: underline;
    text-underline-position: under;
  }
`;

const NavLinks = styled(motion.div)`
  align-items: center;
  background: transparent;
  box-shadow: 0px 0.185rem 0.75rem rgba(0, 0, 0, 0.25);
  color: white;
  display: flex;
  flex-direction: column;
  grid-area: links;
  grid-gap: 0px;
  height: auto;
  justify-content: center;
  ${breakpoints.lg} {
    background: transparent;
    box-shadow: none;
    flex-direction: row;
    grid-column: 6/12;
    height: 100%;
    justify-content: flex-end;
    margin-left: 0;
    margin-right: 0;
    margin-top: 0px;
  }
`;

const DateContainer = styled.div`
  color: white;
  cursor: default;
  font-family: 'Open Sans', sans-serif;
  font-size: 1.25rem;
  font-weight: 300;
  grid-area: date;
  grid-column: 2/8;
  height: auto;
  line-height: 1em;
  margin: 0;
  margin: auto 0;
  text-align: left;
  span {
    font-weight: 700;
  }

  ${breakpoints.lg} {
    grid-column: 2/8;
    height: auto;
  }
`;

const Wrapper = styled(motion.nav)`
  background: 'white;';
  background: ${({ colors }) => colors.secondary};
  box-shadow: none;
  box-shadow: 0px 0.185rem 0.75rem rgba(0, 0, 0, 0.15);
  box-shadow: 0px 8px 8px rgba(0, 0, 0, 0.15);
  display: none;
  grid-template-columns: repeat(12, 1fr);
  height: 3.5rem;
  position: fixed;
  top: 3.8rem;
  transition-duration: 0.75s;
  width: 100%;
  z-index: 3;
  ${breakpoints.lg} {
    align-items: center;
    display: grid;
    grid-template-areas: 'date links';
  }
`;

export default EventNavbar;
